import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import Modal from '../ui/Modal';
import Input from '../ui/Input';
import Button from '../ui/Button';
import Spinner from '../ui/Spinner';
import { useAuth } from '../../context/AuthContext';
import { parseApiError } from '../../utils/apiError';

// Requests a password-reset token. The server responds uniformly (no account
// enumeration); in non-prod it returns a `devResetToken` we surface as a
// convenience deep-link to the reset page.
export default function ForgotPasswordModal({ isOpen, onClose, onSwitchToLogin }) {
  const { requestPasswordReset } = useAuth();

  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [result, setResult] = useState(null); // { devResetToken? }

  const close = () => {
    setEmail('');
    setSubmitting(false);
    setFormError(null);
    setFieldErrors({});
    setResult(null);
    onClose();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);
    setFieldErrors({});
    try {
      const data = await requestPasswordReset(email.trim());
      setResult(data || {});
      setSubmitting(false);
    } catch (err) {
      const { formError: fe, fieldErrors: fld } = parseApiError(err);
      setFormError(fe);
      setFieldErrors(fld);
      setSubmitting(false);
    }
  };

  const devToken = result?.devResetToken;

  return (
    <Modal isOpen={isOpen} onClose={close} title="Reset your password">
      {result ? (
        <div className="ss-form">
          <p className="ss-form__success">
            If an account exists for that email, we’ve sent password-reset
            instructions.
          </p>
          {devToken && (
            <p className="ss-authswitch">
              Dev shortcut:{' '}
              <Link to={`/reset-password?token=${encodeURIComponent(devToken)}`} onClick={close}>
                open reset page
              </Link>
            </p>
          )}
          <Button variant="ghost" onClick={onSwitchToLogin}>
            Back to log in
          </Button>
        </div>
      ) : (
        <form className="ss-form" onSubmit={onSubmit} noValidate>
          {formError && <p className="ss-form__error" role="alert">{formError}</p>}
          <p style={{ color: 'var(--ss-brown-hover)' }}>
            Enter your email and we’ll send you a link to reset your password.
          </p>
          <Input
            label="Email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={fieldErrors.email}
            required
          />
          <div className="ss-form__actions">
            <Button type="submit" disabled={submitting}>
              {submitting ? <Spinner label="Sending" /> : 'Send reset link'}
            </Button>
            <p className="ss-authswitch">
              <button type="button" className="ss-linkbtn" onClick={onSwitchToLogin}>
                Back to log in
              </button>
            </p>
          </div>
        </form>
      )}
    </Modal>
  );
}
